import { Box, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlexCol } from '@/UI/styles';
import { TextLgMd, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger';
import { useGlobalStore } from '@/state/global.state';
import useWorkspacesStore from '@/state/workspaces.state';
import {
  createByokExternalProviderConfig,
  deleteByokExternalProviderConfig,
  getByokExternalProviderConfigs,
  updateByokExternalProviderConfig,
  type BYOKExternalProviderConfigResponseDto,
} from '@/api/byokExternalProviderConfigs.api';
import { API_KEY_PROVIDERS, type ApiKeyProviderId } from './apiKeyProviders';
import { ApiKeysTable } from './ApiKeysTable';
import { ApiKeyDeleteConfirmationModal } from './ApiKeyDeleteConfirmationModal';
import { ProviderTermsDisclaimerModal } from './ProviderTermsDisclaimerModal';
import type { ServerRow, GoogleServiceAccountDraft } from './apiKeysTable.types';

const logger = log.getLogger('ApiKeysSection');

const KNOWN_PROVIDER_IDS = new Set<ApiKeyProviderId>(API_KEY_PROVIDERS.map((p) => p.id));

function mapResponseToRow(config: BYOKExternalProviderConfigResponseDto): ServerRow {
  if (config.serviceAccount != null) {
    return {
      credentialType: 'serviceAccount',
      clientEmail: config.serviceAccount.clientEmail,
      projectId: config.serviceAccount.projectId,
      maskedPrivateKey: config.serviceAccount.privateKey,
      isEnabled: config.isEnabled,
    };
  }
  return {
    credentialType: 'apiKey',
    maskedApiKey: config.apiKey ?? '',
    isEnabled: config.isEnabled,
  };
}

function mapListToState(rows: BYOKExternalProviderConfigResponseDto[]): Partial<Record<ApiKeyProviderId, ServerRow>> {
  const out: Partial<Record<ApiKeyProviderId, ServerRow>> = {};
  for (const row of rows) {
    if (!KNOWN_PROVIDER_IDS.has(row.provider as ApiKeyProviderId)) {
      continue;
    }
    const id = row.provider as ApiKeyProviderId;
    out[id] = mapResponseToRow(row);
  }
  return out;
}

const EMPTY_SERVICE_ACCOUNT_DRAFT: GoogleServiceAccountDraft = { clientEmail: '', projectId: '', privateKey: '' };

function initialServiceAccountDraftForEdit(existing: ServerRow | undefined): GoogleServiceAccountDraft {
  if (existing?.credentialType === 'serviceAccount') {
    return {
      clientEmail: existing.clientEmail,
      projectId: existing.projectId,
      privateKey: '',
    };
  }
  return { ...EMPTY_SERVICE_ACCOUNT_DRAFT };
}

function isDraftEmpty(draft: string | GoogleServiceAccountDraft | undefined, isServiceAccount: boolean): boolean {
  if (isServiceAccount) {
    const sa = typeof draft === 'object' && draft != null ? draft : null;
    return !sa || (!sa.clientEmail.trim() && !sa.projectId.trim() && !sa.privateKey.trim());
  }
  return typeof draft !== 'string' || !draft.trim();
}

function isDraftComplete(draft: string | GoogleServiceAccountDraft | undefined, isServiceAccount: boolean): boolean {
  if (isServiceAccount) {
    const sa = typeof draft === 'object' && draft != null ? draft : null;
    return !!sa && !!sa.clientEmail.trim() && !!sa.projectId.trim() && !!sa.privateKey.trim();
  }
  return typeof draft === 'string' && !!draft.trim();
}

export const ApiKeysSection = () => {
  const { t } = useTranslation();
  const workspaceId = useWorkspacesStore((state) => state.activeWorkspace.workspaceId);
  const updateSnackbarData = useGlobalStore((state) => state.updateSnackbarData);

  const [serverConfigs, setServerConfigs] = useState<Partial<Record<ApiKeyProviderId, ServerRow>>>({});
  const [pendingEnabledByProvider, setPendingEnabledByProvider] = useState<Partial<Record<ApiKeyProviderId, boolean>>>(
    {},
  );
  const [draftByProvider, setDraftByProvider] = useState<
    Partial<Record<ApiKeyProviderId, string | GoogleServiceAccountDraft>>
  >({});
  const [editingByProvider, setEditingByProvider] = useState<Partial<Record<ApiKeyProviderId, boolean>>>({});
  const [isLoadingKeys, setIsLoadingKeys] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [savingProviderId, setSavingProviderId] = useState<ApiKeyProviderId | null>(null);
  const [providerAwaitingApproval, setProviderAwaitingApproval] = useState<ApiKeyProviderId | null>(null);
  const [providerPendingDelete, setProviderPendingDelete] = useState<ApiKeyProviderId | null>(null);
  const [hasSeenDisclaimerByProvider, setHasSeenDisclaimerByProvider] = useState<
    Partial<Record<ApiKeyProviderId, boolean>>
  >({});

  const inputRefs = useRef<Partial<Record<ApiKeyProviderId, HTMLInputElement | null>>>({});

  const showActionErrorSnackbar = useCallback(() => {
    updateSnackbarData({
      text: t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.OPERATION_FAILED),
      isOpen: true,
      icon: null,
      severity: 'error',
    });
  }, [t, updateSnackbarData]);

  const loadConfigs = useCallback(async () => {
    if (!workspaceId) {
      setServerConfigs({});
      setIsLoadingKeys(false);
      setLoadFailed(false);
      return;
    }
    setIsLoadingKeys(true);
    setLoadFailed(false);
    try {
      const rows = await getByokExternalProviderConfigs(workspaceId);
      setServerConfigs(mapListToState(rows));
    } catch {
      setLoadFailed(true);
      showActionErrorSnackbar();
    } finally {
      setIsLoadingKeys(false);
    }
  }, [showActionErrorSnackbar, workspaceId]);

  useEffect(() => {
    void loadConfigs();
  }, [loadConfigs]);

  const handleDraftKeyChange = useCallback((providerId: ApiKeyProviderId, value: string) => {
    setDraftByProvider((prev) => ({ ...prev, [providerId]: value }));
  }, []);

  const handleDraftFieldChange = useCallback(
    (providerId: ApiKeyProviderId, field: keyof GoogleServiceAccountDraft, value: string) => {
      setDraftByProvider((prev) => {
        const existing = prev[providerId];
        const base: GoogleServiceAccountDraft =
          existing != null && typeof existing === 'object' ? existing : { ...EMPTY_SERVICE_ACCOUNT_DRAFT };
        return { ...prev, [providerId]: { ...base, [field]: value } };
      });
    },
    [],
  );

  const focusInputSoon = useCallback((providerId: ApiKeyProviderId) => {
    queueMicrotask(() => {
      inputRefs.current[providerId]?.focus();
    });
  }, []);

  const startEdit = useCallback(
    (providerId: ApiKeyProviderId) => {
      const providerDef = API_KEY_PROVIDERS.find((p) => p.id === providerId);
      setEditingByProvider((prev) => ({ ...prev, [providerId]: true }));
      if (providerDef?.credentialType === 'serviceAccount') {
        setDraftByProvider((prev) => ({
          ...prev,
          [providerId]: initialServiceAccountDraftForEdit(serverConfigs[providerId]),
        }));
      } else {
        setDraftByProvider((prev) => ({ ...prev, [providerId]: '' }));
      }
      focusInputSoon(providerId);
    },
    [focusInputSoon, serverConfigs],
  );

  const requestAddKey = useCallback(
    (providerId: ApiKeyProviderId) => {
      if (hasSeenDisclaimerByProvider[providerId]) {
        startEdit(providerId);
        return;
      }
      setProviderAwaitingApproval(providerId);
    },
    [hasSeenDisclaimerByProvider, startEdit],
  );

  const closeProviderTermsModal = useCallback(() => {
    setProviderAwaitingApproval(null);
  }, []);

  const confirmProviderTerms = useCallback(() => {
    if (!providerAwaitingApproval) {
      return;
    }
    const providerId = providerAwaitingApproval;
    setProviderAwaitingApproval(null);
    setHasSeenDisclaimerByProvider((prev) => ({ ...prev, [providerId]: true }));
    startEdit(providerId);
  }, [providerAwaitingApproval, startEdit]);

  const clearEditState = useCallback((providerId: ApiKeyProviderId) => {
    setEditingByProvider((prev) => ({ ...prev, [providerId]: false }));
    setDraftByProvider((prev) => {
      const next = { ...prev };
      delete next[providerId];
      return next;
    });
  }, []);

  const handleCancelEdit = useCallback(
    (providerId: ApiKeyProviderId) => {
      clearEditState(providerId);
      if (!serverConfigs[providerId]) {
        setPendingEnabledByProvider((prev) => {
          const next = { ...prev };
          delete next[providerId];
          return next;
        });
      }
    },
    [clearEditState, serverConfigs],
  );

  const handleSave = useCallback(
    async (providerId: ApiKeyProviderId) => {
      if (!workspaceId) {
        clearEditState(providerId);
        return;
      }
      const draft = draftByProvider[providerId];
      const curConfig = serverConfigs[providerId];
      const isServiceAccount = API_KEY_PROVIDERS.find((p) => p.id === providerId)?.credentialType === 'serviceAccount';

      if (isDraftEmpty(draft, isServiceAccount)) {
        if (curConfig) clearEditState(providerId);
        return;
      }
      if (!isDraftComplete(draft, isServiceAccount)) return;

      setSavingProviderId(providerId);
      try {
        let res: BYOKExternalProviderConfigResponseDto;
        if (isServiceAccount) {
          const sa = draft as GoogleServiceAccountDraft;
          const serviceAccount = {
            clientEmail: sa.clientEmail.trim(),
            projectId: sa.projectId.trim(),
            privateKey: sa.privateKey.trim(),
          };
          res = curConfig
            ? await updateByokExternalProviderConfig(workspaceId, providerId, { serviceAccount })
            : await createByokExternalProviderConfig(workspaceId, {
                provider: providerId,
                serviceAccount,
                isEnabled: pendingEnabledByProvider[providerId] ?? true,
              });
        } else {
          const apiKey = (draft as string).trim();
          res = curConfig
            ? await updateByokExternalProviderConfig(workspaceId, providerId, { apiKey })
            : await createByokExternalProviderConfig(workspaceId, {
                provider: providerId,
                apiKey,
                isEnabled: pendingEnabledByProvider[providerId] ?? true,
              });
        }
        setServerConfigs((prev) => ({ ...prev, [providerId]: mapResponseToRow(res) }));
        setPendingEnabledByProvider((prev) => {
          const next = { ...prev };
          delete next[providerId];
          return next;
        });
        clearEditState(providerId);
      } catch (e) {
        logger.error('handleSave', e);
        showActionErrorSnackbar();
      } finally {
        setSavingProviderId(null);
      }
    },
    [clearEditState, draftByProvider, pendingEnabledByProvider, serverConfigs, showActionErrorSnackbar, workspaceId],
  );

  const confirmDeleteKey = useCallback(async () => {
    const providerId = providerPendingDelete;
    if (!providerId) {
      return;
    }
    clearEditState(providerId);

    const existing = serverConfigs[providerId];
    if (!existing || !workspaceId) {
      setPendingEnabledByProvider((prev) => {
        const next = { ...prev };
        delete next[providerId];
        return next;
      });
      setProviderPendingDelete(null);
      return;
    }

    setSavingProviderId(providerId);
    try {
      await deleteByokExternalProviderConfig(workspaceId, providerId);
      setServerConfigs((prev) => {
        const next = { ...prev };
        delete next[providerId];
        return next;
      });
      setPendingEnabledByProvider((prev) => {
        const next = { ...prev };
        delete next[providerId];
        return next;
      });
    } catch (e) {
      logger.error('handleDeleteKey', e);
      showActionErrorSnackbar();
    } finally {
      setSavingProviderId(null);
      setProviderPendingDelete(null);
    }
  }, [clearEditState, providerPendingDelete, serverConfigs, showActionErrorSnackbar, workspaceId]);

  const requestDeleteKey = useCallback(
    (providerId: ApiKeyProviderId) => {
      if (savingProviderId) {
        return;
      }
      setProviderPendingDelete(providerId);
    },
    [savingProviderId],
  );

  const handleStatusChange = useCallback(
    async (providerId: ApiKeyProviderId, nextOn: boolean) => {
      const existing = serverConfigs[providerId];
      if (!existing) {
        setPendingEnabledByProvider((prev) => ({ ...prev, [providerId]: nextOn }));
        return;
      }
      if (!workspaceId) {
        return;
      }
      setSavingProviderId(providerId);
      try {
        const res = await updateByokExternalProviderConfig(workspaceId, providerId, { isEnabled: nextOn });
        setServerConfigs((prev) => ({ ...prev, [providerId]: mapResponseToRow(res) }));
      } catch (e) {
        logger.error('handleStatusChange', e);
        showActionErrorSnackbar();
      } finally {
        setSavingProviderId(null);
      }
    },
    [serverConfigs, showActionErrorSnackbar, workspaceId],
  );

  const handleSaveWithVoid = useCallback(
    (providerId: ApiKeyProviderId) => {
      void handleSave(providerId);
    },
    [handleSave],
  );

  const handleStatusChangeWithVoid = useCallback(
    (providerId: ApiKeyProviderId, nextOn: boolean) => {
      void handleStatusChange(providerId, nextOn);
    },
    [handleStatusChange],
  );

  const closeDeleteModal = useCallback(() => {
    setProviderPendingDelete(null);
  }, []);

  const confirmDeleteModal = useCallback(() => {
    void confirmDeleteKey();
  }, [confirmDeleteKey]);

  return (
    <FlexCol sx={{ width: '100%', maxWidth: 920, gap: 3, pb: 1 }}>
      <FlexCol sx={{ gap: 1, pt: '1.5px' }}>
        <TextLgMd>{t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.PAGE_TITLE)}</TextLgMd>
        <TextSmRg color={color.White64_T}>
          {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.PAGE_DESCRIPTION)}
        </TextSmRg>
      </FlexCol>

      {isLoadingKeys ? (
        <Box sx={{ py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : loadFailed ? (
        <TextSmRg color={color.White64_T}>{t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.LOAD_FAILED)}</TextSmRg>
      ) : (
        <ApiKeysTable
          serverConfigs={serverConfigs}
          editingByProvider={editingByProvider}
          draftByProvider={draftByProvider}
          pendingEnabledByProvider={pendingEnabledByProvider}
          savingProviderId={savingProviderId}
          inputRefs={inputRefs}
          onDraftKeyChange={handleDraftKeyChange}
          onDraftFieldChange={handleDraftFieldChange}
          onSave={handleSaveWithVoid}
          onDeleteKey={requestDeleteKey}
          onStartAdd={requestAddKey}
          onStartEdit={startEdit}
          onCancelEdit={handleCancelEdit}
          onStatusChange={handleStatusChangeWithVoid}
        />
      )}
      <ProviderTermsDisclaimerModal
        open={providerAwaitingApproval !== null}
        onClose={closeProviderTermsModal}
        onConfirm={confirmProviderTerms}
      />
      <ApiKeyDeleteConfirmationModal
        open={providerPendingDelete !== null}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteModal}
        isLoading={providerPendingDelete !== null && savingProviderId === providerPendingDelete}
      />
    </FlexCol>
  );
};
